/**
 * CheckboxOption Component
 *
 * Extracted from Figma Crop Circle System
 * Checkbox Option node
 *
 * A selection control that supports checkbox and radio modes.
 * Control is displayed on the left with label text on the right.
 */

import React from 'react';
import { Pressable, StyleSheet, Text, type ViewStyle } from 'react-native';
import { Circle, Path, Svg } from 'react-native-svg';
import { Colors } from '../../../../themes/colors';
import { colors, spacing, tokens } from '../../tokens';

export type CheckboxOptionMode = 'checkbox' | 'radio';

export type CheckboxState = 'checked' | 'unchecked' | 'indeterminate';

export interface CheckboxOptionProps {
  /**
   * Selection mode
   * @default 'checkbox'
   */
  mode?: CheckboxOptionMode;

  /**
   * Current state of the control
   * @default 'unchecked'
   */
  state?: CheckboxState;

  /**
   * Label text displayed to the right of the control
   */
  label?: string;

  /**
   * Callback when the control is pressed
   */
  onPress?: (nextState: CheckboxState) => void;

  /**
   * Disabled state
   * @default false
   */
  disabled?: boolean;

  /**
   * Background color for the container
   */
  backgroundColor?: string;

  /**
   * Custom container style
   */
  style?: ViewStyle;

  /**
   * Horizontal alignment of the label text
   * @default 'left'
   */
  textAlign?: 'left' | 'center' | 'right';

  /**
   * Test identifier
   */
  testID?: string;
}

const CONTROL_SIZE = 18;

const CheckboxIcon = ({
  state,
  disabled,
}: {
  state: CheckboxState;
  disabled: boolean;
}) => {
  const isActive = state !== 'unchecked';
  const fillColor = disabled
    ? Colors.role.surfaceActive
    : isActive
    ? Colors.role.surfaceInverse
    : Colors.role.canvas;
  const strokeColor = disabled
    ? Colors.role.borderControl
    : isActive
    ? Colors.role.surfaceInverse
    : Colors.role.textSoft;

  return (
    <Svg
      width={CONTROL_SIZE}
      height={CONTROL_SIZE}
      viewBox="0 0 18 18"
      fill="none"
    >
      <Path d="M0.75 0.75H16.75V16.75H0.75V0.75Z" fill={fillColor} />
      <Path
        d="M0.75 0.75H16.75V16.75H0.75V0.75Z"
        stroke={strokeColor}
        strokeWidth={1.5}
        strokeLinejoin="round"
      />
      {state === 'checked' && (
        <Path
          d="M4.25 8.75L7.25 11.75L13.25 5.75"
          stroke={Colors.role.textOnInverse}
          strokeWidth={1.5}
        />
      )}
      {state === 'indeterminate' && (
        <Path
          d="M4.75 8.75H12.75"
          stroke={Colors.role.textOnInverse}
          strokeWidth={1.5}
          strokeLinejoin="round"
        />
      )}
    </Svg>
  );
};

const RadioIcon = ({
  state,
  disabled,
}: {
  state: CheckboxState;
  disabled: boolean;
}) => {
  const isActive = state === 'checked';
  const strokeColor = disabled
    ? Colors.role.borderControl
    : isActive
    ? Colors.role.surfaceInverse
    : Colors.role.textSoft;

  return (
    <Svg
      width={CONTROL_SIZE}
      height={CONTROL_SIZE}
      viewBox="0 0 18 18"
      fill="none"
    >
      <Path
        d="M0.75 8.75C0.75 4.33172 4.33172 0.75 8.75 0.75C13.1683 0.75 16.75 4.33172 16.75 8.75C16.75 13.1683 13.1683 16.75 8.75 16.75C4.33172 16.75 0.75 13.1683 0.75 8.75Z"
        fill={Colors.role.canvas}
      />
      <Path
        d="M0.75 8.75C0.75 4.33172 4.33172 0.75 8.75 0.75C13.1683 0.75 16.75 4.33172 16.75 8.75C16.75 13.1683 13.1683 16.75 8.75 16.75C4.33172 16.75 0.75 13.1683 0.75 8.75Z"
        stroke={strokeColor}
        strokeWidth={1.5}
        strokeLinejoin="round"
      />
      {isActive && (
        <Circle
          cx={8.75}
          cy={8.75}
          r={4}
          fill={disabled ? Colors.role.surfaceActive : Colors.role.surfaceInverse}
        />
      )}
    </Svg>
  );
};

export const CheckboxOption: React.FC<CheckboxOptionProps> = ({
  mode = 'checkbox',
  state = 'unchecked',
  label,
  onPress,
  disabled = false,
  backgroundColor,
  style,
  textAlign = 'left',
  testID,
}) => {
  const handlePress = () => {
    if (disabled) return;
    const nextState: CheckboxState =
      state === 'checked' ? 'unchecked' : 'checked';
    onPress?.(nextState);
  };

  const Control =
    mode === 'checkbox' ? (
      <CheckboxIcon state={state} disabled={disabled} />
    ) : (
      <RadioIcon state={state} disabled={disabled} />
    );

  return (
    <Pressable
      style={[
        styles.container,
        { backgroundColor: backgroundColor ?? Colors.role.inputBackground },
        disabled && styles.containerDisabled,
        style,
      ]}
      onPress={handlePress}
      disabled={disabled}
      testID={testID}
      accessibilityRole={mode === 'checkbox' ? 'checkbox' : 'radio'}
      accessibilityState={{ checked: state === 'checked', disabled }}
    >
      {Control}
      {label && (
        <Text
          style={[
            tokens.typography.label.large,
            styles.label,
            { textAlign },
            disabled && styles.labelDisabled,
          ]}
        >
          {label}
        </Text>
      )}
    </Pressable>
  );
};

CheckboxOption.displayName = 'CheckboxOption';

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 4,
  },
  containerDisabled: {
    opacity: 0.7,
  },
  label: {
    flex: 1,
    color: Colors.role.textPrimary,
    lineHeight: 18,
  },
  labelDisabled: {
    color: Colors.role.textSoft,
  },
});
